import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  FileText, Search, Filter, RefreshCw, Eye, Award, XCircle,
  CheckCircle2, Clock, Loader2, DollarSign
} from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VendorQuotationList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [quotations, setQuotations] = useState([]);
  const [filteredQuotations, setFilteredQuotations] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  const loadQuotations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/vendor-quotes/quotations`);
      setQuotations(response.data);
    } catch (error) {
      console.error('Error loading quotations:', error);
      toast.error('Failed to load vendor quotations');
    } finally {
      setLoading(false);
    }
  }, []);

  const filterQuotations = useCallback(() => {
    let filtered = [...quotations];
    if (statusFilter !== 'ALL') {
      filtered = filtered.filter(q => q.status === statusFilter);
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(q =>
        q.quotation_number?.toLowerCase().includes(term) ||
        q.rfq_number?.toLowerCase().includes(term) ||
        q.vendor_id?.toLowerCase().includes(term) ||
        q.vendor_name?.toLowerCase().includes(term)
      );
    }
    setFilteredQuotations(filtered);
  }, [quotations, statusFilter, searchTerm]);

  useEffect(() => {
    loadQuotations();
  }, [loadQuotations]);

  useEffect(() => {
    filterQuotations();
  }, [filterQuotations]);

  const getStatusColor = (status) => {
    const colors = {
      'DRAFT': 'bg-gray-100 text-gray-800',
      'SUBMITTED': 'bg-orange-100 text-orange-800',
      'UNDER_REVIEW': 'bg-yellow-100 text-yellow-800',
      'ACCEPTED': 'bg-green-100 text-green-800',
      'AWARDED': 'bg-emerald-100 text-emerald-800',
      'REJECTED': 'bg-red-100 text-red-800',
      'EXPIRED': 'bg-gray-100 text-gray-500'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'DRAFT': return <Clock className="w-4 h-4" />;
      case 'SUBMITTED': return <FileText className="w-4 h-4" />;
      case 'AWARDED': return <Award className="w-4 h-4" />;
      case 'REJECTED': return <XCircle className="w-4 h-4" />;
      case 'ACCEPTED': return <CheckCircle2 className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="vendor-quotation-list">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <FileText className="w-7 h-7 text-orange-600" />
              Vendor Quotations
            </h1>
            <p className="text-sm text-gray-500">RFQ responses from vendors</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/p2p/rfq-list')}>
              View RFQs
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px] max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search by quotation, RFQ, vendor..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="ALL">All Status</option>
                <option value="SUBMITTED">Submitted</option>
                <option value="UNDER_REVIEW">Under Review</option>
                <option value="AWARDED">Awarded</option>
                <option value="REJECTED">Rejected</option>
              </select>
            </div>
            <Button variant="outline" size="sm" onClick={loadQuotations}>
              <RefreshCw className="w-4 h-4 mr-1" />Refresh
            </Button>
          </div>
        </div>

        {/* Results */}
        <div className="bg-white rounded-lg shadow-sm border">
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="w-12 h-12 animate-spin text-orange-600 mx-auto mb-4" />
              <p className="text-gray-600">Loading vendor quotations...</p>
            </div>
          ) : filteredQuotations.length === 0 ? (
            <div className="p-8 text-center">
              <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-600">No vendor quotations found</p>
              <p className="text-sm text-gray-500 mt-2">Vendors submit quotations in response to RFQs</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quotation #</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">RFQ #</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vendor</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total Value</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Validity</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">PO Created</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredQuotations.map((quote) => (
                    <tr key={quote.quotation_number} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-orange-600">{quote.quotation_number}</td>
                      <td className="px-4 py-3">
                        <button 
                          onClick={() => navigate(`/p2p/rfq/${quote.rfq_number}`)}
                          className="text-orange-600 hover:underline"
                        >
                          {quote.rfq_number}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">{quote.vendor_id}</p>
                        <p className="text-xs text-gray-500 truncate max-w-[150px]">{quote.vendor_name}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(quote.status)}`}>
                          {getStatusIcon(quote.status)}
                          {quote.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-medium">
                        {formatCurrency(quote.total_net_value, quote.currency)}
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-sm">
                        {quote.validity_start} - {quote.validity_end}
                      </td>
                      <td className="px-4 py-3">
                        {quote.po_number ? (
                          <button 
                            onClick={() => navigate(`/p2p/po/${quote.po_number}`)}
                            className="text-green-600 hover:underline font-medium"
                          >
                            {quote.po_number}
                          </button>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/vendor-quotes/${quote.quotation_number}`)}
                          className="text-orange-600 hover:text-orange-700"
                        >
                          <Eye className="w-4 h-4 mr-1" />View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-4 text-sm text-gray-500">
          Showing {filteredQuotations.length} of {quotations.length} quotations
        </div>
      </div>
    </Layout>
  );
};

export default VendorQuotationList;
